<template>
  <v-container fluid class="ebf-page pa-4 pa-md-6">
    <FundPageHeader
      title="Fund Accounts"
      subtitle="Independent mini-ledger accounts for PF, gratuity, loans and benefit payments"
      icon="mdi-book-open-variant-outline"
    >
      <v-btn color="white" variant="tonal" prepend-icon="mdi-refresh" :loading="loading" @click="loadAccounts">Refresh</v-btn>
      <v-btn v-if="$insertAccess($route.fullPath)" color="amber-lighten-3" prepend-icon="mdi-plus" @click="openCreate">New account</v-btn>
    </FundPageHeader>

    <v-card class="ebf-section-card" elevation="0">
      <v-card-text>
        <v-row class="ebf-filter-bar pa-3 mb-4" align="center">
          <v-col cols="12" md="4">
            <v-text-field v-model="search" label="Search account" prepend-inner-icon="mdi-magnify" density="compact" variant="outlined" hide-details clearable />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="filters.fund_type" :items="fundTypes" label="Fund type" density="compact" variant="outlined" hide-details clearable @update:model-value="loadAccounts"  :menu-props="{ contentClass: 'my-custom-dropdown' }"/>
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="filters.role" :items="roleFilters" label="Account role" density="compact" variant="outlined" hide-details clearable @update:model-value="loadAccounts"  :menu-props="{ contentClass: 'my-custom-dropdown' }"/>
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model="filters.to_date" type="date" label="Balance date" density="compact" variant="outlined" hide-details @change="loadAccounts" />
          </v-col>
        </v-row>

        <v-data-table
          class="ebf-table"
          :headers="headers"
          :items="accounts"
          :loading="loading"
          :search="search"
          :items-per-page="15"
          density="compact"
          hover
        >
          <template #item.name="{ item }">
            <div class="font-weight-medium">{{ item.name }}</div>
            <div v-if="item.employee" class="text-caption ebf-muted">{{ employeeName(item.employee) }}</div>
          </template>
          <template #item.role="{ item }">{{ humanize(item.role) }}</template>
          <template #item.fund_type="{ item }"><v-chip size="small" variant="tonal" color="primary">{{ humanize(item.fund_type) }}</v-chip></template>
          <template #item.balance="{ item }"><span class="ebf-money font-weight-medium">{{ money(item.balance) }}</span></template>
          <template #item.status="{ item }">
            <StatusChip :value="item.status ? 'ACTIVE' : 'INACTIVE'" />
          </template>
          <template #item.actions="{ item }">
            <v-btn icon="mdi-book-open-page-variant-outline" size="small" variant="text" color="primary" title="View ledger" @click="openLedger(item)" />
            <v-btn
              v-if="$updateAccess($route.fullPath) && ['BANK', 'MANUAL'].includes(item.role)"
              icon="mdi-pencil-outline"
              size="small"
              variant="text"
              color="warning"
              title="Edit account"
              @click="openEdit(item)"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="accountDialog" max-width="760" persistent>
      <v-card>
        <v-card-title class="ebf-dialog-title d-flex justify-space-between align-center">
          <span>{{ editingId ? "Edit fund account" : "Create fund account" }}</span>
          <v-btn icon="mdi-close" variant="text" color="white" @click="accountDialog = false" />
        </v-card-title>
        <v-card-text class="pa-5">
          <v-form ref="accountFormRef" v-model="accountValid">
            <v-row>
              <v-col cols="12" md="7">
                <v-text-field v-model="accountForm.name" label="Account name" variant="outlined" density="compact" :rules="[required]" />
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field v-model="accountForm.code" label="Account code" variant="outlined" density="compact" :disabled="Boolean(editingId)" :rules="[required]" />
              </v-col>
              <v-col cols="12" md="4">
                <v-select v-model="accountForm.role" :items="createRoles" label="Role" variant="outlined" density="compact" :disabled="Boolean(editingId)" :rules="[required]" @update:model-value="handleRoleChange"  :menu-props="{ contentClass: 'my-custom-dropdown' }"/>
              </v-col>
              <v-col cols="12" md="4">
                <v-select v-model="accountForm.account_type" :items="accountTypes" label="Account type" variant="outlined" density="compact" :disabled="Boolean(editingId) || accountForm.role === 'BANK'" :rules="[required]"  :menu-props="{ contentClass: 'my-custom-dropdown' }"/>
              </v-col>
              <v-col cols="12" md="4">
                <v-select v-model="accountForm.fund_type" :items="fundTypes" label="Fund type" variant="outlined" density="compact" :disabled="Boolean(editingId)" :rules="[required]"  :menu-props="{ contentClass: 'my-custom-dropdown' }"/>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="accountForm.payment_method"
                  :items="paymentMethods"
                  label="Payment method"
                  variant="outlined"
                  density="compact"
                  :disabled="Boolean(editingId) && accountForm.role !== 'BANK'"
                  :rules="accountForm.role === 'BANK' ? [required, bankPaymentRule] : []"
                
                    :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
              </v-col>
              <v-col v-if="!editingId" cols="12" md="6">
                <v-text-field model-value="0.00" label="Opening balance" variant="outlined" density="compact" readonly hint="Use a receipt voucher to record opening fund money." persistent-hint />
              </v-col>
              <v-col cols="12">
                <v-switch v-model="accountForm.status" color="success" label="Active account" hide-details />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4 justify-end">
          <v-btn variant="text" @click="accountDialog = false">Cancel</v-btn>
          <v-btn color="primary" prepend-icon="mdi-content-save-outline" :loading="saving" @click="saveAccount">Save account</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="ledgerDialog" max-width="1100">
      <v-card>
        <v-card-title class="ebf-dialog-title d-flex justify-space-between align-center">
          <div>
            <div>{{ ledgerData?.account?.code }} — {{ ledgerData?.account?.name }}</div>
            <div class="text-caption">Independent fund ledger</div>
          </div>
          <v-btn icon="mdi-close" variant="text" color="white" @click="ledgerDialog = false" />
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row class="ebf-filter-bar pa-2 mb-4" align="center">
            <v-col cols="12" sm="4"><v-text-field v-model="ledgerFilters.from_date" type="date" label="From date" variant="outlined" density="compact" hide-details /></v-col>
            <v-col cols="12" sm="4"><v-text-field v-model="ledgerFilters.to_date" type="date" label="To date" variant="outlined" density="compact" hide-details /></v-col>
            <v-col cols="12" sm="4"><v-btn color="primary" prepend-icon="mdi-magnify" :loading="ledgerLoading" @click="loadLedger">Load ledger</v-btn></v-col>
          </v-row>
          <v-row class="mb-2">
            <v-col cols="12" sm="6"><MetricCard label="Opening balance" :value="money(ledgerData?.opening_balance)" icon="mdi-arrow-collapse-left" color="blue-grey" /></v-col>
            <v-col cols="12" sm="6"><MetricCard label="Closing balance" :value="money(ledgerData?.closing_balance)" icon="mdi-arrow-collapse-right" color="teal" /></v-col>
          </v-row>
          <v-data-table :headers="ledgerHeaders" :items="ledgerData?.entries || []" :loading="ledgerLoading" :items-per-page="15" density="compact">
            <template #item.date="{ item }">{{ displayDate(item.voucher?.voucher_date) }}</template>
            <template #item.voucher="{ item }">{{ item.voucher?.code || '-' }}</template>
            <template #item.employee="{ item }">{{ employeeName(item.employee) }}</template>
            <template #item.debit="{ item }"><span class="ebf-money">{{ item.transaction_type === 'DEBIT' ? money(item.amount) : '-' }}</span></template>
            <template #item.credit="{ item }"><span class="ebf-money">{{ item.transaction_type === 'CREDIT' ? money(item.amount) : '-' }}</span></template>
            <template #item.running_balance="{ item }"><span class="ebf-money font-weight-bold">{{ money(item.running_balance) }}</span></template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import { toast } from "vue3-toastify";
import employeeBenefitFundsApi from "@/services/employeeBenefitFundsApi";
import FundPageHeader from "./components/FundPageHeader.vue";
import MetricCard from "./components/MetricCard.vue";
import StatusChip from "./components/StatusChip.vue";
import { apiError, compactParams, displayDate, employeeName, envelopeData, humanize, money } from "./utils";

const loading = ref(false);
const saving = ref(false);
const search = ref("");
const accounts = ref([]);
const accountDialog = ref(false);
const editingId = ref(null);
const accountFormRef = ref(null);
const accountValid = ref(false);
const ledgerDialog = ref(false);
const ledgerLoading = ref(false);
const ledgerAccountId = ref(null);
const ledgerData = ref(null);

const filters = reactive({ fund_type: null, role: null, to_date: null });
const ledgerFilters = reactive({ from_date: null, to_date: null });
const blankAccount = () => ({ name: "", code: "", role: "BANK", account_type: "ASSET", fund_type: "PF", payment_method: "BANK", opening_balance: 0, status: true });
const accountForm = reactive(blankAccount());

const fundTypes = ["PF", "GRATUITY", "MIXED"];
const accountTypes = ["ASSET", "LIABILITY", "EQUITY", "INCOME", "EXPENSE"];
const paymentMethods = ["NONE", "CASH", "BANK", "MOBILE_BANKING"];
const createRoles = [{ title: "Fund bank/cash account", value: "BANK" }, { title: "Manual ledger account", value: "MANUAL" }];
const roleFilters = ["BANK", "PAYROLL_CLEARING", "PF_EMPLOYEE", "PF_COMPANY", "PF_FORFEITURE", "GRATUITY_CORPUS", "EMPLOYEE_LOAN", "MEDICAL_EXPENSE", "GRATUITY_SETTLEMENT", "MANUAL"];

const headers = [
  { title: "Code", key: "code" },
  { title: "Account", key: "name" },
  { title: "Role", key: "role" },
  { title: "Fund", key: "fund_type" },
  { title: "Type", key: "account_type" },
  { title: "Payment", key: "payment_method" },
  { title: "Balance", key: "balance", align: "end" },
  { title: "Status", key: "status", align: "center" },
  { title: "Action", key: "actions", align: "center", sortable: false },
];
const ledgerHeaders = [
  { title: "Date", key: "date" },
  { title: "Voucher", key: "voucher" },
  { title: "Employee", key: "employee" },
  { title: "Remarks", key: "remarks" },
  { title: "Debit", key: "debit", align: "end" },
  { title: "Credit", key: "credit", align: "end" },
  { title: "Balance", key: "running_balance", align: "end" },
];

const required = (value) => value !== null && value !== undefined && value !== "" || "Required";
const bankPaymentRule = (value) => value !== "NONE" || "Select a cash, bank or mobile banking method";

const loadAccounts = async () => {
  loading.value = true;
  try {
    accounts.value = envelopeData(await employeeBenefitFundsApi.accounts(compactParams(filters))) || [];
  } catch (exception) {
    toast.error(apiError(exception, "Unable to load fund accounts."));
  } finally {
    loading.value = false;
  }
};

const resetAccountForm = () => {
  Object.assign(accountForm, blankAccount());
  editingId.value = null;
};

const openCreate = () => {
  resetAccountForm();
  accountDialog.value = true;
};

const openEdit = (item) => {
  editingId.value = item.id;
  Object.assign(accountForm, {
    name: item.name,
    code: item.code,
    role: item.role,
    account_type: item.account_type,
    fund_type: item.fund_type,
    payment_method: item.payment_method,
    opening_balance: item.opening_balance,
    status: Boolean(item.status),
  });
  accountDialog.value = true;
};

const handleRoleChange = (role) => {
  if (role === "BANK") {
    accountForm.account_type = "ASSET";
    if (accountForm.payment_method === "NONE") accountForm.payment_method = "BANK";
  } else {
    accountForm.payment_method = "NONE";
  }
};

const saveAccount = async () => {
  const validation = await accountFormRef.value?.validate();
  if (!validation?.valid) return;
  saving.value = true;
  try {
    if (editingId.value) {
      await employeeBenefitFundsApi.updateAccount(editingId.value, {
        name: accountForm.name,
        payment_method: accountForm.payment_method,
        status: accountForm.status,
      });
    } else {
      await employeeBenefitFundsApi.createAccount({ ...accountForm, opening_balance: 0 });
    }
    toast.success(editingId.value ? "Fund account updated." : "Fund account created.");
    accountDialog.value = false;
    await loadAccounts();
  } catch (exception) {
    toast.error(apiError(exception, "Unable to save the fund account."));
  } finally {
    saving.value = false;
  }
};

const openLedger = async (item) => {
  ledgerAccountId.value = item.id;
  ledgerData.value = { account: item, entries: [], opening_balance: 0, closing_balance: 0 };
  ledgerDialog.value = true;
  await loadLedger();
};

const loadLedger = async () => {
  if (!ledgerAccountId.value) return;
  ledgerLoading.value = true;
  try {
    ledgerData.value = envelopeData(await employeeBenefitFundsApi.accountLedger(ledgerAccountId.value, compactParams(ledgerFilters)));
  } catch (exception) {
    toast.error(apiError(exception, "Unable to load the account ledger."));
  } finally {
    ledgerLoading.value = false;
  }
};

onMounted(loadAccounts);
</script>
